import { Link, useNavigate, useParams } from "react-router-dom"
import MainLayout from "../../components/layouts/MainLayout"
import { useEffect, useState } from "react"
import { Alert, Spin, Collapse, Button, message, Tag, Popconfirm, Space } from "antd"
import { PlusCircleOutlined, LeftCircleOutlined, EditOutlined } from '@ant-design/icons';
import { DeviseApi } from "../../api/deviseApi"
import { DateFormat } from "../../utils/formatDate"
const { Panel } = Collapse;

const DetailsDeviseView = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [devise, setDevise] = useState(null)
    const [loading, setLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [error, setError] = useState(null);
    const abortController = new AbortController()

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await DeviseApi.getDeviseDetails(id, abortController.signal);
            setDevise(res.data);
        } catch (err) {
            setError("Impossible de charger les détails de la devise.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleActivate = async () => {
        setActionLoading(true);
        try {
            const res = await DeviseApi.activateDevise(id, abortController.signal);
            res.success ? message.success(res.message) : message.error(res.message);
            fetchData();
        } catch (error) {
            message.error("Échec de l'activation");
        } finally {
            setActionLoading(false);
        }
    }

    const handleDeactivate = async () => {
        setActionLoading(true);
        try {
            const res = await DeviseApi.deactivateDevise(id, abortController.signal);
            res.success ? message.success(res.message) : message.error(res.message);
            fetchData();
        } catch (error) {
            message.error("Échec de la désactivation");
        } finally {
            setActionLoading(false);
        }
    }

    const handleSetDefault = async () => {
        setActionLoading(true);
        try {
            const res = await DeviseApi.setDefaultDevise(id, abortController.signal);
            res.success ? message.success(res.message) : message.error(res.message);
            fetchData();
        } catch (error) {
            message.error("Échec de la définition par défaut");
        } finally {
            setActionLoading(false);
        }
    }

    const handleDelete = async () => {
        setActionLoading(true);
        try {
            const res = await DeviseApi.deleteDevise(id, abortController.signal);
            if (!res.success) {
                setActionLoading(false);
                return message.error(res.message);
            }
            message.success(res.message);
            navigate('/devises');
        } catch (error) {
            setActionLoading(false);
            message.error("Échec de la suppression");
        }
    }

    return <MainLayout>
        <div className="container mx-auto p-6">
            <div className="flex justify-between">
                <h1 className="text-3xl font-semibold mb-6">Détails de la devise {devise?.name}</h1>
                <Link to={`/devises`}><LeftCircleOutlined /> Retour  </Link>
            </div>

            {loading && <Spin size="large" />}
            {error && <Alert message={error} type="error" />}
            {!loading && !error && devise && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <Collapse defaultActiveKey={['1']} expandIconPosition="start">
                        <Panel header="Informations générales" key="1" extra={<PlusCircleOutlined />}>
                            <div className="space-y-4">
                                <p><strong>Nom :</strong> {devise?.name}</p>
                                <p><strong>Code ISO :</strong> {devise?.iso_code}</p>
                                <p><strong>Symbole :</strong> {devise?.symbol} ({devise?.symbol_position === 'before' ? 'avant le montant' : 'après le montant'})</p>
                                <p><strong>Pays / région :</strong> {devise?.country_region || 'Non renseigné'}</p>
                                <p><strong>Nombre de décimales :</strong> {devise?.decimal_places}</p>
                                <p><strong>Devise de référence :</strong> {devise?.is_default ? <Tag color="gold">Oui</Tag> : <Tag>Non</Tag>}</p>
                                <p><strong>Statut :</strong> {devise?.is_active ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}</p>
                            </div>
                        </Panel>
                    </Collapse>

                    <Collapse defaultActiveKey={['2']} expandIconPosition="start" className="mt-4">
                        <Panel header="Taux de change" key="2" extra={<PlusCircleOutlined />}>
                            <div className="space-y-4">
                                <p><strong>Taux (vs devise de référence) :</strong> {Number(devise?.exchange_rate).toFixed(8)}</p>
                                <p><strong>Mode :</strong> {devise?.rate_mode === 'AUTO' ? 'Automatique (Frankfurter)' : 'Manuel'}</p>
                                <p><strong>Source :</strong> {devise?.rate_source || 'Non renseigné'}</p>
                                <p><strong>Dernière synchronisation :</strong> {devise?.last_synced_at ? DateFormat.formatDate(devise.last_synced_at) : 'Jamais'}</p>
                            </div>
                        </Panel>
                    </Collapse>

                    <div className="mt-6">
                        <Space wrap>
                            <Button icon={<EditOutlined />} onClick={() => navigate(`/devises/${id}/modifier`)}>
                                Modifier
                            </Button>

                            {!devise?.is_default && !devise?.is_active && (
                                <Button type="primary" loading={actionLoading} onClick={handleActivate}>
                                    Activer
                                </Button>
                            )}

                            {!devise?.is_default && devise?.is_active && (
                                <Button loading={actionLoading} onClick={handleDeactivate}>
                                    Désactiver
                                </Button>
                            )}

                            {!devise?.is_default && (
                                <Button loading={actionLoading} onClick={handleSetDefault}>
                                    Définir par défaut
                                </Button>
                            )}

                            {!devise?.is_default && (
                                <Popconfirm title="Supprimer cette devise ?" onConfirm={handleDelete} okText="Oui" cancelText="Non">
                                    <Button danger loading={actionLoading}>
                                        Supprimer
                                    </Button>
                                </Popconfirm>
                            )}
                        </Space>
                    </div>
                </div>
            )}
        </div>
    </MainLayout>
}
export default DetailsDeviseView
